/**
 * ConversationSelector — sidebar panel that lets the operator pick reference
 * conversations the assistant should look at when proposing changes (Story
 * 8.5 AC1 + AC8).
 *
 * Reuses `ConversationFilters` from Story 7.10 verbatim so the filter UX
 * stays identical to `/conversations`. The list itself is rendered here as
 * checkbox rows — operator multi-selects, the parent page passes the
 * resulting IDs to `chatTurn(referenceConversationIds)`.
 *
 * LGPD (AC8): rows expose ONLY `maskedPhone` + campaign + status + relative
 * time + message count. The full transcript is NOT rendered here — the
 * operator can't accidentally read raw customer content from this flow.
 * Server-side phone masking (Story 7.10 AC) is the source of truth; this
 * component never receives unmasked phones.
 */

import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError, apiClient } from '@/lib/api';
import { formatDate } from '@/lib/format-date';
import {
  ConversationFilters,
  EMPTY_FILTERS,
  toQueryString,
  type ConversationFilterValue,
} from '@/components/conversations/ConversationFilters';
import { StatusBadge, type ConversationStatus } from '@/components/conversations/StatusBadge';

interface ConversationListItem {
  id: string;
  customerId: string;
  maskedPhone: string;
  campaignId: string;
  campaignName: string | null;
  status: ConversationStatus;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
}

interface ListResponse {
  items: ConversationListItem[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 10;

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h`;
  const diffDays = Math.round(diffSec / 86400);
  if (diffDays < 30) return `${diffDays}d`;
  return formatDate(iso);
}

function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `Erro HTTP ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return 'Erro desconhecido';
}

export interface ConversationSelectorProps {
  /** Currently selected conversation IDs (controlled). */
  selectedIds: string[];
  /** Emitted when the operator toggles a row. */
  onToggle: (id: string) => void;
  /** Optional cap; UI shows a warning when reaching the limit. */
  maxSelectable?: number;
}

export function ConversationSelector({
  selectedIds,
  onToggle,
  maxSelectable = 10,
}: ConversationSelectorProps) {
  const [filters, setFilters] = useState<ConversationFilterValue>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const qs = toQueryString(filters, page, PAGE_SIZE);
  const listQuery = useQuery<ListResponse>({
    queryKey: ['admin', 'assistant-conversation-selector', qs],
    queryFn: () => apiClient.get<ListResponse>(`/admin/conversations?${qs}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const atCap = selectedIds.length >= maxSelectable;

  return (
    <div className="flex h-full flex-col gap-3" data-testid="conversation-selector">
      <ConversationFilters
        value={filters}
        onApply={(next) => {
          setFilters(next);
          setPage(1);
        }}
        onClear={() => {
          setFilters(EMPTY_FILTERS);
          setPage(1);
        }}
      />

      <Card className="flex-1 overflow-hidden">
        <CardHeader className="py-3">
          <CardTitle className="flex items-center justify-between text-sm">
            <span>Conversas</span>
            <span className="text-xs font-normal text-muted-foreground">
              {selectedIds.length}/{maxSelectable} selecionadas
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent
          className="space-y-2 overflow-y-auto p-3"
          style={{ maxHeight: 'calc(100vh - 360px)' }}
        >
          {listQuery.isLoading ? (
            <div className="text-xs text-muted-foreground">Carregando…</div>
          ) : null}
          {listQuery.isError ? (
            <div className="rounded border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              {humanizeError(listQuery.error)}
            </div>
          ) : null}
          {!listQuery.isLoading && items.length === 0 ? (
            <div className="text-xs text-muted-foreground">Nenhuma conversa encontrada.</div>
          ) : null}
          {items.map((item) => {
            const checked = selectedIds.includes(item.id);
            const disabled = !checked && atCap;
            return (
              <label
                key={item.id}
                data-testid={`conversation-row-${item.id}`}
                className={`flex cursor-pointer items-start gap-2 rounded border p-2 text-xs transition-colors ${
                  checked
                    ? 'border-primary bg-primary/5'
                    : disabled
                      ? 'border-input opacity-50'
                      : 'border-input hover:bg-accent'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggle(item.id)}
                  aria-label={`Selecionar conversa ${item.maskedPhone}`}
                />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px]">{item.maskedPhone}</span>
                    <StatusBadge status={item.status} />
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="truncate">{item.campaignName ?? '—'}</span>
                    <span>
                      {item.messageCount} msg · {formatRelative(item.lastMessageAt)}
                    </span>
                  </div>
                </div>
              </label>
            );
          })}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          ← Anterior
        </Button>
        <span>
          Página {page} de {totalPages}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Próxima →
        </Button>
      </div>
    </div>
  );
}
